<template>
  <div>
    <div v-if="showInitialLoading" class="filters-initial-loading">
      <q-spinner-dots size="32px" color="primary" />
      <span class="loading-text">Chargement des filtres...</span>
    </div>

    <div v-else class="tw-saved-filters modern-saved-filters">
      <div class="tw-saved-filters-inner modern-filters-grid" :style="gridStyle">
        <template v-for="(filter, index) in listOfFilters" :key="filter.resourceUid || index">
          <div
            v-if="isAuthorized(userAuthorities, filter.groupFilter?.items) || isAdmin"
            class="tw-saved-filters-box modern-filter-card"
            :data-filter-id="filter.resourceUid"
          >
            <FilterButton
              :filter="filter"
              :connectedUser="connectedUser"
              :index="index"
              :batchComplete="batchCountComplete"
              :isAdmin="isAdmin"
              @click="onSelectedFilter(filter)"
              @delete="onDeleteFilter(filter)"
              @edit="onEditFilter(filter)"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import store from "@/store";
import { isFilterDAAQMatching } from "@/store/services/filter/filterService";
import { Options, Vue } from "vue-class-component";
import FilterButton from "./FilterButton.vue";
import { isAuthorized } from "./utils/utils";
import { getGroupsByUserId } from "@/store/services/task/taskService";
import { Watch } from "vue-property-decorator";
import { getQueryIdFromRoute } from "@/utils/filterUtils";


const userAuthoritiesCache: { data: string[] | null } = { data: null };
const batchLoadingCache: { hasLoaded: boolean } = { hasLoaded: false };

function getExpectedQueryId(path: string): string {
  if (path.includes('/third')) {
    return 'party-filter-search';
  } else if (path.includes('/task')) {
    return 'search-task';
  } else if (path === '/order') {
    return 'order-search-filter';
  }

  return '';
}

@Options({
  props: ["query", "columns"],
  methods: {
    isAuthorized
  },
  components: {
    FilterButton
  },
  watch: {
    listOfFilters: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          // Check if all filters have finished loading (countLoading === false)
          const allLoaded = newVal.every((f: any) => f.countLoading === false);
          if (allLoaded) {
            this.$nextTick(() => {
              this.batchCountComplete = true;
              batchLoadingCache.hasLoaded = true;
            });
          }
        } else {
          // Reset cache when filters are cleared
          this.batchCountComplete = false;
          batchLoadingCache.hasLoaded = false;
        }
      },
      deep: true,
      immediate: true
    },
    '$route'(newRoute, oldRoute) {
      if (newRoute.path !== oldRoute.path) {
        this.batchCountComplete = false;
        batchLoadingCache.hasLoaded = false;
        userAuthoritiesCache.data = null;
        store.commit('filterModule/setListOfFilters', []);
        this.$nextTick(() => {
          setTimeout(() => {
            this.refreshFilters();
          }, 50);
        });
      }
    },
    '$store.state.authModule.selectedUserNetwork'(
      newValue: { value: string } | null
    ) {
      if (newValue?.value) {
        batchLoadingCache.hasLoaded = false;
        this.batchCountComplete = false;

        // Réinitialisation des autorités (spécifiques au réseau)
        userAuthoritiesCache.data = null;

        // Nettoyage des filtres pour éviter les données obsolètes
        store.commit('filterModule/setListOfFilters', []);

        // Rechargement des filtres pour la route courante
        this.refreshFilters();
      }
    }
  }
})
export default class FilterList extends Vue {
  userAuthorities: string[] = [];
  batchCountComplete = false;

  visibleFilterIds: Set<string> = new Set();
  intersectionObserver: IntersectionObserver | null = null;

  get isAdmin() {
    return this.userAuthorities.includes('GRP_ADMIN');
  }

  get isLoading() {
    return store.getters["filterModule/getIsLoading"];
  }

  get showInitialLoading() {
    return this.isLoading && this.listOfFilters.length === 0;
  }

  get listOfFilters() {
    const allFilters = store.getters["filterModule/listOfFilters"];
    const expectedQueryId = getExpectedQueryId(this.$route.path);

    return allFilters.filter((filter: any) => {
      const filterScreenId = filter.screenId || filter.businessData?.screenId || '';
      return filterScreenId === expectedQueryId;
    });
  }

  get currentUserDAAQ() {
    return store.getters["authModule/getDefaultDAAQConfig"];
  }

  get connectedUser() {
    return store.getters["authModule/getUserConnected"];
  }

  columns!: number;

  get gridStyle() {
    const cols = this.columns || 6;
    return {
      gridTemplateColumns: `repeat(${cols}, 1fr)`
    };
  }

  /**
   * Searches for order based on the filter selected.
   * @return {void}
   */
  onSelectedFilter(filter: any) {
    store.dispatch("filterModule/setSelectedFilter", filter);
  }

  onDeleteFilter(filter: any) {
    store.dispatch("showConfirmationDeleteDialog", {
      message: `Are you sure you want to delete the filter ${filter.description}?`,
      deleteAction: 'QueryFilter',
      payload: filter,
      confirmation: true
    })
  }

  onEditFilter(filter: any) {
    store.dispatch("filterModule/editSelectedFilter", filter);
  }

  checkFilterActionPermission(isActionAllowed: boolean, filterDaaq: string): boolean {
    const { readDAAQ, defaultCreateDAAQ } = this.currentUserDAAQ;
    return isActionAllowed && isFilterDAAQMatching(filterDaaq, { readDAAQ, defaultCreateDAAQ });
  }

  setColor(color: string) {
    return "background: " + color;
  }

  refreshFilters() {
    const queryId = getQueryIdFromRoute(this.$route.path);
    const expectedQueryId = getExpectedQueryId(this.$route.path);
    if (queryId && queryId === expectedQueryId) {
      store.dispatch('filterModule/getSavedFilters', { queryId });
    }
  }

  fetchUserGroups() {
    if (!this.connectedUser || !this.connectedUser.userId) {
      this.userAuthorities = [];
      userAuthoritiesCache.data = null;
      return;
    }

    if (userAuthoritiesCache.data !== null) {
      this.userAuthorities = userAuthoritiesCache.data;
      return;
    }

    const userGroups = getGroupsByUserId();
    this.userAuthorities = userGroups.items.map((g: any) => g.label);
    userAuthoritiesCache.data = this.userAuthorities;
  }
  
  setupIntersectionObserver() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const filterId = (entry.target as HTMLElement).dataset.filterId;
          if (filterId) {
            if (entry.isIntersecting) {
              this.visibleFilterIds.add(filterId);
            } else {
              this.visibleFilterIds.delete(filterId);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      }
    );
  }

  observeFilterCards() {
    if (!this.intersectionObserver) return;

    this.$nextTick(() => {
      const cards = document.querySelectorAll('.modern-filter-card[data-filter-id]');
      cards.forEach((card) => {
        this.intersectionObserver?.observe(card);
      });
    });
  }

  cleanupObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.visibleFilterIds.clear();
  }

  mounted() {
    this.fetchUserGroups();
    this.refreshFilters();
    this.setupIntersectionObserver();

    this.$watch('listOfFilters', () => {
      this.$nextTick(() => {
        this.observeFilterCards();
      });
    }, { immediate: true });
  }

  beforeUnmount() {
    this.cleanupObserver();
  }
}
</script>
<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&display=swap');

.filters-initial-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 16px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;

  .loading-text {
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    font-family: 'Work Sans', sans-serif;
  }
}

.modern-saved-filters {
  background: transparent;
  padding: 0;
  margin-bottom: 16px;
  font-family: 'Work Sans', sans-serif;
  width: 100%;
  overflow: hidden;
}

.modern-filters-grid {
  display: grid;
  padding: 0;
  margin: 0;
  gap: 0;
  width: 100%;
}

.modern-filter-card {
  display: flex;
  align-items: stretch;
  min-width: 0;
  overflow: visible;
}

.filter-box {
  display: flex;
  align-items: stretch;
}

.filter-btn {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

li {
  display: inline;
}

li:nth-child(3n):after {
  content: "\a";
  white-space: pre;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
